import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from '@helpers/api';

import { IconListDetails, IconPencil, IconTestPipe, IconTrash } from "@tabler/icons-react";


export default function useIntegrations(info) {
    const { t } = useTranslation("integrations");
    const [loading, setLoading] = useState(true);
    const [integrations, setIntegrations] = useState([]);
    const [pagination, setPagination] = useState({ pages: 0, records: 0, page: 1 });

    const popperRef = useRef();
    const manageModalRef = useRef();
    const logsModalRef = useRef();

    useEffect(() => {
        if (info?.id) handleLoad(1);
    }, [info?.id]);

    const handleLoad = useCallback(async (page = pagination.page) => {
        setLoading(true);
        try {
            if (info?.id) {
                const res = await api.get(`community/${info.id}/plan/integration/list`, { page });
                if (res?.response?.status >= 400) {
                    setIntegrations([]);
                } else {
                    const raw = res?.data;
                    const list = Array.isArray(raw) ? raw : raw?.data ?? [];
                    setIntegrations([...list]);
                    setPagination({
                        pages: raw?.pages ?? raw?.last_page ?? 0,
                        records: raw?.records ?? raw?.total ?? list.length,
                        page: raw?.page ?? raw?.current_page ?? page,
                    });
                }
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }, [info?.id]);

    // `platform` (a PLATFORMS key) preselects it in the create modal.
    function handleOpenCreate(platform) {
        manageModalRef.current?.openCreate(() => handleLoad(1), platform);
    }

    function handleOpenOptions(event, integration) {
        const options = [
            {
                id: 'edit',
                label: t('actions.edit'),
                icon: <IconPencil size={16} />,
                onClick: async () => {
                    popperRef.current.close();
                    manageModalRef.current?.openEdit(integration, () => handleLoad(pagination.page));
                }
            },
            {
                id: 'test',
                label: t('actions.test'),
                icon: <IconTestPipe size={16} />,
                onClick: async () => {
                    popperRef.current.close();
                    handleTestIntegration(integration);
                }
            },
            {
                id: 'logs',
                label: t('actions.logs'),
                icon: <IconListDetails size={16} />,
                onClick: async () => {
                    popperRef.current.close();
                    logsModalRef.current?.open(integration);
                }
            },
            {
                id: 'delete',
                label: t('actions.delete'),
                icon: <IconTrash size={16} />,
                variant: 'danger',
                onClick: async () => {
                    popperRef.current.close();
                    handleDelete(integration);
                }
            },
        ];
        popperRef.current.open(options, 'bottom', event.currentTarget);
    }

    async function handleTestIntegration(integration) {
        window.setSnackbar(t('feedback.testing'));
        try {
            const res = await api.post(`integration/${integration.id}/test`);
            if (res?.response?.status >= 400) {
                window.setSnackbar(res?.response?.data?.message || t('feedback.testError'));
                return;
            }
            // The logs viewer loads newest-first and auto-selects the first
            // entry, so it opens on the execution the test just performed.
            logsModalRef.current?.open(integration);
        } catch (e) {
            console.error(e);
            window.setSnackbar(t('feedback.testError'));
        }
    }

    async function handleToggle(integration) {
        const previousActive = !!integration.active;

        // Optimistically update the UI
        setIntegrations(prev => prev.map(item =>
            item.id === integration.id ? { ...item, active: !previousActive } : item
        ));

        try {
            const res = await api.patch(`integration/${integration.id}/toggle`);
            if (res?.response?.status >= 400) {
                throw new Error(res?.response?.data?.message || t('manage.errors.toggleStatus'));
            }
            window.setSnackbar(!previousActive ? t('feedback.activated') : t('feedback.deactivated'));
        } catch (e) {
            console.error(e);
            window.setSnackbar(e.message || t('feedback.statusError'));
            // Revert state if API call fails
            setIntegrations(prev => prev.map(item =>
                item.id === integration.id ? { ...item, active: previousActive } : item
            ));
        }
    }

    function handleDelete(integration) {
        window.openConfirm({
            title: t('delete.title'),
            headline: t('settings:shared.warning'),
            subtitle: t('delete.subtitle', { name: integration.name }),
            buttons: [
                {
                    text: t('delete.confirm'),
                    variant: "contained",
                    color: "error",
                    onClick: async () => {
                        try {
                            const res = await api.delete(`integration/${integration.id}`);
                            if (res?.response?.status >= 400) {
                                window.setSnackbar(res?.response?.data?.message || t('feedback.deleteError'));
                                return;
                            }
                            window.setSnackbar(t('feedback.deleted'));
                            await handleLoad(pagination.page);
                        } catch (e) {
                            console.log(e);
                        }
                    }
                },
                { text: t('common:actions.cancel'), variant: "text" }
            ]
        });
    }

    return {
        loading,
        communityLoading: false,
        integrations,
        pagination,
        popperRef,
        manageModalRef,
        logsModalRef,
        community: info,
        handleLoad,
        handleToggle,
        handleDelete,
        handleOpenCreate,
        handleOpenOptions,
    };
}
